import { useEffect, useRef, useState } from "react";
import type { TechRequestConfig } from "../scenario/types";
import { audio } from "../lib/audio";

/**
 * The visitor is now INSIDE, standing by the desk, asking to "just check" the
 * PC. We never show him — his voice + captions come from the right with a live
 * speaking indicator. The learner allows it (the mistake) or pushes back, which
 * only produces more confident pressure.
 */
export function TechRequest({
  config,
  onAllow,
  onDeny,
}: {
  config: TechRequestConfig;
  onAllow: () => void;
  /** Safe exit: the learner stays firm after the pressure line. */
  onDeny?: () => void;
}) {
  const [shown, setShown] = useState(0);
  const [speaking, setSpeaking] = useState(false);
  const [pressured, setPressured] = useState(false);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    let cancelled = false;
    // SEQUENTIAL: reveal a caption, speak it, and only move to the next line once
    // the voice finishes — so lines never overlap into multiple voices. speak()
    // resolves on ended/error/timeout, so this also can't stall.
    (async () => {
      const lang = config.language ?? "en-US";
      const voice = config.voiceId ?? "onyx";
      // Prefetch all lines so captions and voice start together.
      void Promise.all(config.lines.map((t) => audio.prefetchSpeech(t, { voice, lang })));
      for (let i = 0; i < config.lines.length; i++) {
        if (cancelled) return;
        setSpeaking(true);
        await audio.speak(config.lines[i], {
          voice,
          lang,
          onStart: () => {
            if (!cancelled) setShown(i + 1);
          },
        });
        if (cancelled) return;
        setSpeaking(false);
        await new Promise((r) => window.setTimeout(r, 300));
      }
    })();
    return () => {
      cancelled = true;
      audio.cancelSpeech();
    };
  }, [config]);

  const ready = shown >= config.lines.length;

  return (
    <div className="techreq">
      <div className="tr-panel">
        <div className="tr-head">
          <span className="tr-ava" aria-hidden>
            {"\u{1F464}"}
          </span>
          <div className="tr-id">
            <strong>{config.speakerName}</strong>
            {config.speakerOrg && <span>{config.speakerOrg}</span>}
          </div>
          <span className={`tr-speak ${speaking ? "on" : ""}`} aria-hidden>
            <i />
            <i />
            <i />
          </span>
        </div>

        <div className="tr-req">{config.request}</div>

        <div className="tr-lines">
          {config.lines.slice(0, shown).map((l, i) => (
            <div key={i} className="tr-bubble">
              {l}
            </div>
          ))}
          {pressured && config.pressure && (
            <div className="tr-bubble pressure">{config.pressure}</div>
          )}
        </div>

        <div className="tr-actions">
          {!pressured && config.denyLabel && (
            <button
              type="button"
              className="tr-btn ghost"
              disabled={!ready}
              onClick={() => {
                setPressured(true);
                if (config.pressure) {
                  setSpeaking(true);
                  audio
                    .speak(config.pressure, {
                      voice: config.voiceId ?? "onyx",
                      lang: config.language ?? "en-US",
                    })
                    .finally(() => setSpeaking(false));
                }
              }}
            >
              {config.denyLabel}
            </button>
          )}
          {pressured && onDeny && (
            <button
              type="button"
              className="tr-btn ghost"
              onClick={() => {
                audio.cancelSpeech();
                onDeny();
              }}
            >
              {config.holdLabel ?? "Stay firm: call IT first"}
            </button>
          )}
          <button type="button" className="tr-btn allow" disabled={!ready} onClick={onAllow}>
            {config.allowLabel ?? "Let him check"}
          </button>
        </div>
      </div>
    </div>
  );
}
